
import { useState } from "react";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle, XCircle } from "lucide-react";

export function SubscriptionStatusCheck() {
  const [productId, setProductId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const { loading, response, checkSubscriptionStatus } = useSubscriptionStatus();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await checkSubscriptionStatus(productId, schoolId);
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Check Subscription Status</CardTitle>
        <CardDescription>
          Verify if a school has an active subscription for a specific product
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="productId">Product ID</Label>
            <Input
              id="productId"
              placeholder="Enter product ID"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="schoolId">School ID</Label>
            <Input
              id="schoolId"
              placeholder="Enter school ID"
              value={schoolId}
              onChange={(e) => setSchoolId(e.target.value)}
              required
            />
          </div>
          
          {response && (
            <div className={`p-4 rounded-md ${
              !response.success ? 'bg-red-50 text-red-700' : 
              response.active ? 'bg-green-50 text-green-700' : 
              'bg-yellow-50 text-yellow-700'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {!response.success ? (
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  ) : response.active ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-yellow-400" />
                  )}
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium">
                    {!response.success ? 'Error' : 
                     response.active ? 'Active Subscription' : 
                     'No Active Subscription'}
                  </h3>
                  <div className="mt-2 text-sm">
                    {response.error ? (
                      <p>{response.error}</p>
                    ) : response.active ? (
                      <div>
                        <p><strong>Status:</strong> {response.status}</p>
                        <p><strong>Expires:</strong> {response.end_date ? new Date(response.end_date).toLocaleDateString() : 'N/A'}</p>
                        <p><strong>Subscription ID:</strong> {response.subscription_id}</p>
                        {response.token && (
                          <p><strong>Access Token:</strong> {response.token}</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p>{response.message || 'No active subscription found for this product and school'}</p>
                        {response.token && (
                          <p><strong>Access Token:</strong> {response.token}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      </CardContent>
      <CardFooter>
        <Button type="submit" onClick={handleSubmit} disabled={loading}>
          {loading ? "Checking..." : "Check Status"}
        </Button>
      </CardFooter>
    </Card>
  );
}
